import { useNavigate } from "react-router";
import { useAuthContext, AuthContext } from "../../Context/AuthContext";
import { useAppContext } from "../../Context";
import { Constants } from "../../constants/appConstants";
import { Tooltip } from "primereact/tooltip";
import landingImg from "../../assets/Pbc_Banner.svg";
import pbc_insights_center from "../../assets/pbc_insights_center.svg";
import pbc_scoring_portal from "../../assets/pbc_scoring_portal.svg";
import data_upload_center from "../../assets/data_upload_center.svg";
import provider_data_and_notices from "../../assets/provider_data_and_notices.svg";
import std_and_measures_library from "../../assets/std_and_measures_library.svg";
import adminConsoleIcon from "../../assets/adminConsoleIcon.svg";
import ComponentHeader from "../../pages/ComponentHeader";
import "./MainViewPage.scss";
import { useContext, useEffect, useMemo, useRef } from "react";
import { validateOrCreateUser } from "../../services/UserService";
import { AuthenticationFactory } from "../../services/Auth/AuthenticationFactory";

const normalizeProviderType = (value?: string): string => {
  const normalized = String(value || "").trim().toUpperCase();

  if (normalized === Constants.SCOKey) {
    return Constants.SCOKey;
  }

  if (
    normalized === Constants.RSKey ||
    normalized === "RESIDENTIAL" ||
    normalized === "RH"
  ) {
    return Constants.RSKey;
  }

  return Constants.RSKey;
};

export const MainViewPage = () => {
  const navigate = useNavigate();
  const { appUser, isSuperAdministrator } = useAuthContext();
  const { isUserValidated, setIsUserValidated } = useContext(AuthContext);

  const {
    providerType,
    reviewYear
  } = useAppContext();

  const validationInProgressRef = useRef(false);

  const authProvider =
    import.meta.env.VITE_AUTH_PROVIDER?.replace(/['"]+/g, "") || "siteminder";

  const authService = useMemo(() => {
    return AuthenticationFactory.getAuthService(authProvider);
  }, [authProvider]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const error = params.get("error");

      if (error === "access_denied") {
        console.warn(
          "[MainViewPage] SiteMinder access denied."
        );

        sessionStorage.setItem("loginDenied", "true");
        navigate("/siteminder-login");
        return;
      }

      const loginDenied = sessionStorage.getItem("loginDenied");

      if (loginDenied === "true") {
        return;
      }

      const accessToken = sessionStorage.getItem("access_token");

      if (!accessToken) {
        authService.loginWithoutCredentials();
        return;
      }

      if (!isUserValidated && !validationInProgressRef.current) {
        validationInProgressRef.current = true;

        validateOrCreateUser()
          .then(() => {
            setIsUserValidated(true);
          })
          .catch((error) => {
            console.error(
              "[MainViewPage] User validation failed.",
              error
            );
          })
          .finally(() => {
            validationInProgressRef.current = false;
          });
      }
    } catch (error) {
      console.error(
        "[MainViewPage] Authentication validation failed.",
        error
      );
    }
  }, [
    isUserValidated,
    setIsUserValidated,
    navigate,
    authService
  ]);

  const normalizedProviderType = useMemo(() => {
    return normalizeProviderType(providerType);
  }, [providerType]);

  const navigateWithContext = (
    path: string,
    additionalState?: Record<string, unknown>
  ) => {
    try {
      navigate(path, {
        state: {
          providerType: normalizedProviderType,
          reviewYear,
          ...additionalState,
        },
      });
    } catch (error) {
      console.error(
        "[MainViewPage] Navigation failed.",
        error
      );
    }
  };

  const handlePbcInsightsClick = () => {
    const portal =
      normalizedProviderType === Constants.SCOKey
        ? Constants.PBC_TABLEAU_URL_KEY
        : Constants.PBC_RS_TABLEAU_URL_KEY;

    navigateWithContext("/tableau", {
      portal,
    });
  };

  const handlePbcScoringClick = () => {
    navigate(
      `/dashboard?providerType=${normalizedProviderType}`,
      {
        state: {
          providerType: normalizedProviderType,
          reviewYear,
        },
      }
    );
  };

  const handleP4pInsightsClick = () => {
    if (normalizedProviderType === Constants.SCOKey) {
      navigateWithContext("/tableau", {
        portal: Constants.P4P_TABLEAU_URL_KEY,
      });

      return;
    }

    navigate("/coming-soon");
  };

  const handleP4pScoringClick = () => {
    if (normalizedProviderType === Constants.SCOKey) {
      navigate(
        `/pfp/providers?providerType=${normalizedProviderType}`,
        {
          state: {
            providerType: normalizedProviderType,
            reviewYear,
          },
        }
      );

      return;
    }

    navigate("/coming-soon");
  };

  useEffect(() => {
    const adjustTooltipPosition = () => {
      const tooltips = document.querySelectorAll(".p-tooltip");

      tooltips.forEach((tooltip) => {
        if (tooltip instanceof HTMLElement) {
          const rect = tooltip.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          if (rect.right > viewportWidth - 10) {
            tooltip.style.left = `${viewportWidth - rect.width - 20}px`;
          }

          if (rect.left < 10) {
            tooltip.style.left = "10px";
          }

          if (rect.top < 10) {
            tooltip.classList.add("p-tooltip-flipped");
          }

          if (rect.bottom > viewportHeight - 10) {
            tooltip.classList.add("p-tooltip-flipped");
          }
        }
      });
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (
              node instanceof HTMLElement &&
              node.classList.contains("p-tooltip")
            ) {
              setTimeout(adjustTooltipPosition, 0);
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="main-view-page">
      <Tooltip
        target=".mv-pbc-insights-tooltip"
        position="top"
        showDelay={300}
        hideDelay={200}
        className="custom-tooltip-wrapper"
      >
        <span>
          The <b>PBC Insights Center</b> features interactive visualizations and
          real-time data, providing both executive and operational views to
          monitor and analyze key metrics throughout the PBC evaluation process.
          Users can filter and interact with the dashboard to view the status of
          PBC submissions, evaluation outcomes, and drill down to measure-level
          and SCO-level details through customized views.
        </span>
      </Tooltip>

      <Tooltip
        target=".mv-pbc-scoring-tooltip"
        position="top"
        showDelay={300}
        hideDelay={200}
        className="custom-tooltip-wrapper"
      >
        <span>
          The <b>PBC Scoring Portal</b> is designed to facilitate the evaluation
          of SCO data submitted through the PBC Data Submission Tool. The portal
          offers features for scoring and analyzing submission data, including
          rule-based, system-generated recommendations and structured workflows
          for reviewers.
        </span>
      </Tooltip>

      <Tooltip
        target=".mv-standards-tooltip"
        position="top"
        showDelay={300}
        hideDelay={200}
        className="custom-tooltip-wrapper"
      >
        <span>
          The <b>PBC Standards and Measures</b> Library organizes all PBC
          measures by service, performance area, standard, and measure code.
          Users can easily filter measures, access detailed descriptions and
          submission requirements by hovering over each measure, and capture
          comments to track changes and document future plans for each measure.
        </span>
      </Tooltip>

      <Tooltip
        target=".mv-data-upload-tooltip"
        position="top"
        showDelay={300}
        hideDelay={200}
        className="custom-tooltip-wrapper"
      >
        <span>
          The <b>Data Upload Center</b> allows Admin users to upload datasets
          used to evaluate specific PBC measures and P4P criteria. Users can
          upload, view, or replace files, and use filters to view and validate
          submitted files.
        </span>
      </Tooltip>

      <Tooltip
        target=".mv-provider-notices-tooltip"
        position="top"
        showDelay={300}
        hideDelay={200}
        className="custom-tooltip-wrapper"
      >
        <span>
          The <b>Provider Data and Notices</b> center serves as a central hub
          for exporting PBC and P4P data leveraging various filters. It also
          functions as a notification center, enabling users to send PBC and P4P
          notices.
        </span>
      </Tooltip>

      <Tooltip
        target=".mv-p4p-insights-tooltip"
        position="top"
        showDelay={300}
        hideDelay={200}
        className="custom-tooltip-wrapper"
      >
        <span>
          The <b>P4P Insights Center</b> offers interactive visualizations and
          real-time data, delivering both executive and operational perspectives
          to monitor and analyze key metrics for P4P initiatives. Users can
          filter and interact with the dashboard to track the progress of P4P
          Initiative submissions and reviews, monitor review outcomes, and drill
          down to detailed views for deeper analysis.
        </span>
      </Tooltip>

      <Tooltip
        target=".mv-p4p-scoring-tooltip"
        position="top"
        showDelay={300}
        hideDelay={200}
        className="custom-tooltip-wrapper"
      >
        <span>
          The <b>P4P Scoring Portal</b> streamlines the evaluation of P4P
          Initiative data submitted via the PBC Data Submission Tool. The portal
          features tools for scoring and analyzing submission data, including
          rule-based, system-generated recommendations and structured workflows
          to guide reviewers through the evaluation process.
        </span>
      </Tooltip>

      <ComponentHeader
        providerType={providerType}
        reviewYear={reviewYear}
      />

      <div className="mv-banner">
        <img
          src={landingImg}
          className="mv-banner-img"
          alt="Banner"
        />

        <div className="mv-banner-overlay">
          <h1 className="mv-banner-title">
            MyPBC Portal
          </h1>

          <p className="mv-banner-welcome">
            <em>Welcome, {appUser?.name}!</em>
          </p>

          {appUser?.reviewerType && (
            <p className="mv-banner-role">
              {appUser.reviewerType}
            </p>
          )}
        </div>
      </div>

      <div className="mv-banner-stripe" />

      <div className="mv-cards-grid">
        <div className="mv-card mv-card-portal">
          <h2 className="mv-card-title mv-card-title-blue">
            MyPBC Portal
          </h2>

          <p className="mv-card-description">
            Welcome to the MyPBC Portal your one-stop hub for monitoring
            performance, evaluating submissions, and managing measures across
            PBC and P4P.
          </p>

          <p className="mv-card-description">
            Interactive, near real-time insights make it easy to monitor
            submission status, review outcomes, and drill from high-level trends
            down to detail views. Guided scoring workflows streamline reviews
            with rule-based analysis and system-generated recommendations,
            improving consistency and reducing manual effort, work faster and
            with more confidence by bringing the full evaluation lifecycle into
            one place.
          </p>

          <div className="mv-card-actions">
            <button
              className="mv-go-btn mv-contact-btn"
              onClick={() => navigate("/contact")}
            >
              <span className="mv-go-arrow">
                &rarr;
              </span>{" "}
              Contact Us
            </button>
          </div>
        </div>

        <div className="mv-card">
          <h2 className="mv-card-title mv-card-title-blue">
            Performance-Based Contracting (PBC)
          </h2>

          <div className="mv-card-items">
            <div className="mv-card-item">
              <img
                src={pbc_insights_center}
                alt="PBC Insights Center"
                className="mv-card-item-icon mv-pbc-insights-tooltip"
              />

              <div className="mv-card-item-content">
                <h3 className="mv-card-item-title">
                  PBC Insights Center
                </h3>

                <p className="mv-card-item-desc">
                  Filter and interact with multiple views of real-time data,
                  view the status of PBC submissions, and evaluation outcomes.
                </p>
              </div>

              <button
                className="mv-go-btn"
                onClick={handlePbcInsightsClick}
              >
                <span className="mv-go-arrow">
                  &rarr;
                </span>{" "}
                Go
              </button>
            </div>

            <div className="mv-card-item">
              <img
                src={pbc_scoring_portal}
                alt="PBC Scoring Portal"
                className="mv-card-item-icon mv-pbc-scoring-tooltip"
              />

              <div className="mv-card-item-content">
                <h3 className="mv-card-item-title">
                  PBC Scoring Portal
                </h3>

                <p className="mv-card-item-desc">
                  Designed to facilitate the evaluation of agency data submitted
                  through the PBC Data Submission Tool.
                </p>
              </div>

              <button
                className="mv-go-btn"
                onClick={handlePbcScoringClick}
              >
                <span className="mv-go-arrow">
                  &rarr;
                </span>{" "}
                Go
              </button>
            </div>
          </div>
        </div>

        <div className="mv-card">
          <h2 className="mv-card-title mv-card-title-blue">
            General Resources
          </h2>

          <div className="mv-card-items">
            <div className="mv-card-item">
              <img
                src={std_and_measures_library}
                alt="Standards & Measures Library"
                className="mv-card-item-icon mv-standards-tooltip"
              />

              <div className="mv-card-item-content">
                <h3 className="mv-card-item-title">
                  Standards &amp; Measures Library
                </h3>

                <p className="mv-card-item-desc">
                  Organizes all PBC measures by service, performance area,
                  standard, and measure code.
                </p>
              </div>

              <button
                className="mv-go-btn"
                onClick={() => navigateWithContext("/standards")}
              >
                <span className="mv-go-arrow">
                  &rarr;
                </span>{" "}
                Go
              </button>
            </div>

            <div className="mv-card-item">
              <img
                src={data_upload_center}
                alt="Data Upload Center"
                className="mv-card-item-icon mv-data-upload-tooltip"
              />

              <div className="mv-card-item-content">
                <h3 className="mv-card-item-title">
                  Data Upload Center
                </h3>

                <p className="mv-card-item-desc">
                  Upload datasets used to evaluate specific PBC measures and P4P
                  criteria.
                </p>
              </div>

              <button
                className="mv-go-btn"
                onClick={() => navigateWithContext("/dataupload/import")}
              >
                <span className="mv-go-arrow">
                  &rarr;
                </span>{" "}
                Go
              </button>
            </div>

            <div className="mv-card-item">
              <img
                src={provider_data_and_notices}
                alt="Provider Data and Notices"
                className="mv-card-item-icon mv-provider-notices-tooltip"
              />

              <div className="mv-card-item-content">
                <h3 className="mv-card-item-title">
                  Provider Data &amp; Notices
                </h3>

                <p className="mv-card-item-desc">
                  Serves as a central hub for exporting PBC and P4P data
                  leveraging various filters.
                </p>
              </div>

              <button
                className="mv-go-btn"
                onClick={() => navigateWithContext("/dataupload/notice")}
              >
                <span className="mv-go-arrow">
                  &rarr;
                </span>{" "}
                Go
              </button>
            </div>

            {isSuperAdministrator() && (
              <div className="mv-card-item">
                <img
                  src={adminConsoleIcon}
                  alt="Admin Console"
                  className="mv-card-item-icon"
                />

                <div className="mv-card-item-content">
                  <h3 className="mv-card-item-title">
                    Admin Console
                  </h3>

                  <p className="mv-card-item-desc">
                    Manage administrative operations including help documents,
                    user role assignment, and submission-level controls.
                  </p>
                </div>

                <button
                  className="mv-go-btn"
                  onClick={() => navigateWithContext("/admin-console")}
                >
                  <span className="mv-go-arrow">
                    &rarr;
                  </span>{" "}
                  Go
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mv-card">
          <h2 className="mv-card-title mv-card-title-blue">
            Pay-for-Performance (P4P)
          </h2>

          <div className="mv-card-items">
            <div className="mv-card-item">
              <img
                src={pbc_insights_center}
                alt="P4P Insights Center"
                className="mv-card-item-icon mv-p4p-insights-tooltip"
              />

              <div className="mv-card-item-content">
                <h3 className="mv-card-item-title">
                  P4P Insights Center
                </h3>

                <p className="mv-card-item-desc">
                  Offers interactive visualizations and real-time data,
                  delivering both executive and operational perspectives to
                  monitor and analyze key metrics for P4P initiatives.
                </p>
              </div>

              <button
                className="mv-go-btn"
                onClick={handleP4pInsightsClick}
              >
                <span className="mv-go-arrow">
                  &rarr;
                </span>{" "}
                Go
              </button>
            </div>

            <div className="mv-card-item">
              <img
                src={pbc_scoring_portal}
                alt="P4P Scoring Portal"
                className="mv-card-item-icon mv-p4p-scoring-tooltip"
              />

              <div className="mv-card-item-content">
                <h3 className="mv-card-item-title">
                  P4P Scoring Portal
                </h3>

                <p className="mv-card-item-desc">
                  Streamlines the evaluation of P4P Initiative data submitted via
                  the PBC Data Submission Tool.
                </p>
              </div>

              <button
                className="mv-go-btn"
                onClick={handleP4pScoringClick}
              >
                <span className="mv-go-arrow">
                  &rarr;
                </span>{" "}
                Go
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainViewPage;